import { useEffect, useState } from 'react';
import {
  getBenchmarkSnapshot,
  subscribeBenchmark,
} from './benchmark.js';
import { APP_CONFIG } from './config.js';

function formatMs(value) {
  return typeof value === 'number' ? `${Math.round(value)} ms` : '—';
}

function formatSize(bytes) {
  if (!bytes) return '—';
  return bytes >= 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${bytes} B`;
}

function apiLabel(request) {
  if (request.route !== '/u/category/vod/list') return request.route.replace('/u/', '');
  const cond = request.requestBody?.Cond;
  if (cond?.SectionLimit) return `vod/list · first ${cond.SectionLimit}`;
  if (cond?.SectionOffset) return `vod/list · offset ${cond.SectionOffset}`;
  return 'vod/list · full';
}

export default function BenchmarkPanel() {
  const [, setVersion] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [copyLabel, setCopyLabel] = useState('複製完整 JSON');

  useEffect(
    () => subscribeBenchmark(() => setVersion((value) => value + 1)),
    [],
  );

  if (!APP_CONFIG.debugEnabled) return null;

  const snapshot = getBenchmarkSnapshot();
  const firstRendered = snapshot.milestones.first_sections_rendered?.atMs;
  const allRendered = snapshot.milestones.all_sections_rendered?.atMs;

  const copyJson = async () => {
    const json = JSON.stringify(getBenchmarkSnapshot(), null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setCopyLabel('已複製，可直接貼給我');
    } catch {
      window.prompt('複製以下 JSON', json);
      setCopyLabel('已開啟複製視窗');
    }
    window.setTimeout(() => setCopyLabel('複製完整 JSON'), 2200);
  };

  return (
    <aside className={`benchmark-panel ${collapsed ? 'is-collapsed' : ''}`}>
      <header className="benchmark-header">
        <div>
          <span className="benchmark-live" />
          <strong>Benchmark</strong>
          <small>{APP_CONFIG.environment}/{APP_CONFIG.networkPath} · {snapshot.page.apiHost}</small>
        </div>
        <button onClick={() => setCollapsed((value) => !value)}>
          {collapsed ? '展開' : '收合'}
        </button>
      </header>

      {!collapsed ? (
        <div className="benchmark-body">
          <div className="benchmark-summary">
            <span><small>FCP</small><b>{formatMs(snapshot.navigation.firstContentfulPaintMs)}</b></span>
            <span><small>LCP</small><b>{formatMs(snapshot.navigation.largestContentfulPaintMs)}</b></span>
            <span><small>首批可見</small><b>{formatMs(firstRendered)}</b></span>
            <span><small>全部完成</small><b>{formatMs(allRendered)}</b></span>
          </div>

          <div className="benchmark-network">
            <span>網路</span>
            <b>{snapshot.page.connection?.effectiveType ?? 'unknown'}</b>
            <span>RTT</span>
            <b>{formatMs(snapshot.page.connection?.rttMs)}</b>
            <span>下行</span>
            <b>{snapshot.page.connection?.downlinkMbps
              ? `${snapshot.page.connection.downlinkMbps} Mbps`
              : '—'}</b>
          </div>

          <div className="benchmark-api-list">
            {snapshot.apiRequests.length ? snapshot.apiRequests.map((request) => (
              <div className="benchmark-api-row" key={request.id}>
                <span>{apiLabel(request)}</span>
                <b>{formatMs(request.totalMs)}</b>
                <small>TTFB {formatMs(request.ttfbMs)}</small>
                <small>{formatSize(request.wireBytes || request.cipherBytes)}</small>
              </div>
            )) : <p>等待 API 數據…</p>}
          </div>

          <button className="benchmark-copy" onClick={copyJson}>{copyLabel}</button>
          <p className="benchmark-hint">等待「全部完成」出現後複製。JSON 不含密鑰。</p>
        </div>
      ) : null}
    </aside>
  );
}
